import { ElementRef, Injectable } from '@angular/core';
import { Breakpoint, FontModel, TypographySettings, TypographyTheme } from '../models';

import { FontLoaderService } from './font-loader.service';
import { FontsApiService } from './fonts-api.service';
import { FormGroup } from '@angular/forms';
import { TypographyApiService } from './typography-api.service';

@Injectable({
  providedIn: 'root'
})
export class TypographyService {
  public static DEFAULT_THEME_ID = 1;
  public static readonly INHERIT_FROM_LG = 'inherit_from_lg';
  public static readonly FONT_FAMILY_PROPERTY_KEY = 'fontFamily';

  private readonly STYLE_VARIABLE_PREFIX = '--lms-typography';
  private kebabCase = (str: string): string => str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();

  private breakpointSuffixMap = {
    [Breakpoint.MOBILE]: '',
    [Breakpoint.TABLET]: '-md',
    [Breakpoint.DESKTOP]: '-lg',
  };

  private fonts: FontModel[] = [];

  constructor(
    private readonly fontsApiService: FontsApiService,
    private readonly fontLoaderService: FontLoaderService,
    private readonly typographyApiService: TypographyApiService
  ) {
    this.fetchPlatformFonts();
  }

  public fetchPlatformFonts(): void {
    this.fontsApiService.getPlatformFonts()
      .subscribe(fonts => {
        this.fonts = fonts;
      });
  }

  public applyTypographyTheme(theme: TypographyTheme, element: HTMLElement, overrides = {}) {
    if (!theme?.typography || !element) {
      return;
    }

    for (let i = element.style.length - 1; i >= 0; i--) {
      const propName = element.style.item(i);
      if (propName.startsWith(this.STYLE_VARIABLE_PREFIX)) {
        element.style.removeProperty(propName);
      }
    }

    const typography: Record<string, TypographySettings> = { ...theme.typography, ...(overrides ?? {}) };

    for (const [tag, settings] of Object.entries(typography) as [string, Record<string, Record<Breakpoint, string>>][]) {
      for (const [property, breakpoints] of Object.entries(settings)) {
        if (!breakpoints) {
          continue;
        }

        for (const bpKey of Object.keys(breakpoints)) {
          const styleValue = this.getStyleVariableValue(property, breakpoints, bpKey as Breakpoint);
          const suffix = this.breakpointSuffixMap[bpKey as Breakpoint] || '';
          const varName = this.getStyleVariableName(tag, property, suffix);
          element.style.setProperty(varName, styleValue);
        }
      }
    }
  }

  public applyThemeForBreakpoint(theme: TypographyTheme, element: HTMLElement, breakpoint: Breakpoint, overrides = {}) {
    if (!theme?.typography || !element) {
      return;
    }

    const suffixes = Object.values(this.breakpointSuffixMap);

    const typography: Record<string, TypographySettings> = { ...theme.typography, ...(overrides ?? {}) };

    for (const [tag, settings] of Object.entries(typography)) {
      for (const [property, breakpoints] of Object.entries(settings)) {

        if (!breakpoints) {
          continue;
        }

        for (const suffix of suffixes) {
          const styleValue = this.getStyleVariableValue(property, breakpoints, breakpoint);
          const varName = this.getStyleVariableName(tag, property, suffix);

          if (styleValue) {
            element.style.setProperty(varName, styleValue);
          } else {
            element.style.removeProperty(varName);
          }
        }
      }
    }
  }

  public applyTypographyThemeToForm(form: FormGroup, element: ElementRef): void {
    const getLastParentForm = (parentForm) => {
        if (!parentForm.parent) {
        return parentForm;
      }

      return getLastParentForm(parentForm.parent);
    }

    const parentForm = getLastParentForm(form);
    const themeControl = parentForm.get('themeId');
    const typographyOverridesForm = parentForm.get('typographyOverrides') as FormGroup;

    this.typographyApiService.getTypographyThemes()
      .subscribe(data => {
        const typographyThemes = data.items || [];
        const theme = typographyThemes?.find(theme => theme.id === themeControl?.value) || typographyThemes?.[0];
        this.applyTypographyTheme(theme, element.nativeElement, typographyOverridesForm?.value);
      });
  }

  private getStyleVariableName(tag: string, property: string, suffix: string): string {
    return `${this.STYLE_VARIABLE_PREFIX}-${tag}-${this.kebabCase(property)}${suffix}`;;
  }

  private getStyleVariableValue(property: string, values: Record<Breakpoint, string>, breakpoint: Breakpoint): string {
    const currentBreakpointValue = values?.[breakpoint];

    const value = currentBreakpointValue === TypographyService.INHERIT_FROM_LG 
      ? values?.[Breakpoint.DESKTOP] 
      : currentBreakpointValue;

    if (property === TypographyService.FONT_FAMILY_PROPERTY_KEY) {
      const font = this.fonts.find(font => font.id === value);

      if (font) {
        this.fontLoaderService.loadFont(font);
        return font.title ? `"${font.title}"` : null;
      }

      return null;
    }

    return value;
  }
}
